/**
 * SpacesTabStrip — desktop tab strip for navigating between spaces (≥768px).
 * ARIA: role="tablist". Drag-reorder: snapshot-based (see tab-reorder-math.ts).
 * Inline rename: dblclick → input; Enter/Escape settled via renameSettledRef latch.
 * Shortcuts: Alt+Digit1..8 (e.code, layout-independent; ctrlKey guard = AltGr protection).
 * Immutability: pure controlled component; all state via shared logic hook.
 */
import { useRef, useEffect, useCallback } from 'react';
import type { Space } from 'briefpane/schema';
import { MAX_SPACES, MAX_SPACE_NAME_CHARS } from 'briefpane/schema';
import type { SpacesActions } from '../board/use-spaces.js';
import type { SpacesSwitcherLogic } from './use-spaces-switcher.js';
import { SpacePopover } from './SpacePopover.js';
import { SpaceNamePrompt } from './SpaceNamePrompt.js';
import { makeSnapshot, computeReorder } from './tab-reorder-math.js';

interface SpacesTabStripProps {
  readonly spaces: readonly Space[];
  readonly activeSpaceId: string;
  readonly actions: SpacesActions;
  readonly logic: SpacesSwitcherLogic;
  /** If true, keyboard shortcuts are suppressed. */
  readonly isTV: boolean;
}

/** Detect prefers-reduced-motion. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Check if current focus is in an interactive text field (for shortcut guard). */
function focusInTextField(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function SpacesTabStrip({
  spaces,
  activeSpaceId,
  actions,
  logic,
  isTV,
}: SpacesTabStripProps): React.ReactElement {
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inlineInputRef = useRef<HTMLInputElement | null>(null);

  // H2: latch set before Enter/Escape so onBlur no-ops after they settle the rename.
  const renameSettledRef = useRef(false);

  const {
    promptMode,
    promptError,
    popover,
    renameInlineState,
    inlineRenameError,
    handlePromptConfirm,
    handlePromptCancel,
    openPopover,
    handlePopoverRename,
    handlePopoverDuplicate,
    handlePopoverDelete,
    closePopover,
    startInlineRename,
    commitInlineRename,
    cancelInlineRename,
    clearInlineRenameError,
    tabId,
    setPromptMode,
    setPromptError,
  } = logic;

  const atLimit = spaces.length >= MAX_SPACES;

  // Auto-focus inline rename input when it opens.
  useEffect(() => {
    if (renameInlineState && inlineInputRef.current) {
      inlineInputRef.current.select();
    }
  }, [renameInlineState]);

  // C1 fix: actually clear the inline rename error after 3s with proper cleanup.
  useEffect(() => {
    if (!inlineRenameError) return;
    const t = setTimeout(() => clearInlineRenameError(), 3000);
    return () => clearTimeout(t);
  }, [inlineRenameError, clearInlineRenameError]);

  // L1 fix: Alt+1..8 keyboard shortcuts using e.code (Digit1..Digit8) for layout-independence.
  // Guard: !e.altKey || e.metaKey || e.ctrlKey — ctrlKey exclusion is deliberate AltGr protection.
  useEffect(() => {
    if (isTV) return;

    function handleKeyDown(e: KeyboardEvent): void {
      if (!e.altKey || e.metaKey || e.ctrlKey) return;
      // e.code is layout-independent: 'Digit1'..'Digit8' regardless of keyboard locale.
      const match = /^Digit([1-8])$/.exec(e.code);
      if (!match) return;
      if (focusInTextField()) return;

      const idx = parseInt(match[1] ?? '0', 10) - 1;
      const space = spaces[idx];
      if (!space) return;

      e.preventDefault();
      // method='shortcut' measures keyboard shortcut adoption.
      actions.switchSpace(space.id, 'shortcut');
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [isTV, spaces, actions]);

  // Roving tabindex: arrow key navigation within the tablist.
  const handleTabKeyDown = useCallback((e: React.KeyboardEvent, idx: number) => {
    const n = spaces.length;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIdx = (idx + 1) % n;
      const nextTab = tabRefs.current[nextIdx];
      const btn = nextTab?.querySelector<HTMLElement>('[role="tab"]');
      btn?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIdx = (idx - 1 + n) % n;
      const prevTab = tabRefs.current[prevIdx];
      const btn = prevTab?.querySelector<HTMLElement>('[role="tab"]');
      btn?.focus();
    }
  }, [spaces.length]);

  // Drag-to-reorder state (H1 fix: snapshot at drag-start, never during pointermove).
  const dragRef = useRef<{
    active: boolean;
    spaceId: string;
    startX: number;
    originIdx: number;
    targetIdx: number;
    /** Snapshot captured at drag-start (lefts + widths + derived gap). */
    snapshot: ReturnType<typeof makeSnapshot>;
  } | null>(null);

  const handleTabPointerDown = useCallback((e: React.PointerEvent, spaceId: string, idx: number) => {
    // Don't start drag on the options button or inline input.
    if ((e.target as HTMLElement).closest('button.spaces-tab-options') ||
        (e.target as HTMLElement).closest('input')) {
      return;
    }
    // M2 fix: cancel any active inline rename before arming a drag.
    if (logic.renameInlineState) {
      cancelInlineRename();
    }
    dragRef.current = {
      active: false,
      spaceId,
      startX: e.clientX,
      originIdx: idx,
      targetIdx: idx,
      // Snapshot deferred to first pointermove (when we confirm >8px slop),
      // because measuring during pointerdown is fine but we only need it once active.
      snapshot: makeSnapshot([], []),
    };
  }, [logic.renameInlineState, cancelInlineRename]);

  const handleTabPointerMove = useCallback((e: React.PointerEvent, idx: number) => {
    const drag = dragRef.current;
    if (!drag || drag.originIdx !== idx) return;

    const deltaX = e.clientX - drag.startX;
    if (!drag.active && Math.abs(deltaX) < 8) return;

    if (!drag.active) {
      drag.active = true;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

        // Capture snapshot ONCE at activation (H1 fix).
      const lefts: number[] = [];
      const widths: number[] = [];
      tabRefs.current.forEach((el) => {
        const rect = el?.getBoundingClientRect();
        lefts.push(rect?.left ?? 0);
        widths.push(rect?.width ?? 0);
      });
      drag.snapshot = makeSnapshot(lefts, widths);
    }

    // Compute target index and displacements from snapshot.
    const result = computeReorder(drag.snapshot, drag.originIdx, deltaX);
    drag.targetIdx = result.targetIdx;

    const reduced = prefersReducedMotion();
    tabRefs.current.forEach((tabEl, i) => {
      if (!tabEl) return;
      if (i === drag.originIdx) {
        tabEl.classList.add('spaces-tab--dragging'); // L6: class instead of inline opacity
        tabEl.style.transform = `translateX(${deltaX}px)`;
        tabEl.style.transition = '';
        return;
      }
      // M2: skip the wrapper hosting an active rename input.
      if (logic.renameInlineState?.spaceId === spaces[i]?.id) return;
      const offset = result.displacedOffsets[i] ?? 0;
      if (offset !== 0) {
        tabEl.style.transform = `translateX(${offset}px)`;
        if (!reduced) tabEl.style.transition = `transform var(--dur-settle) var(--spring-settle)`;
      } else {
        tabEl.style.transform = '';
        tabEl.style.transition = '';
      }
    });
  }, [spaces, logic.renameInlineState]);

  const handleTabPointerUp = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;

    // Reset all transforms and class.
    tabRefs.current.forEach((tabEl) => {
      if (!tabEl) return;
      tabEl.classList.remove('spaces-tab--dragging');
      tabEl.style.transform = '';
      tabEl.style.transition = '';
    });

    if (!drag.active) return;

    // Commit reorder if position changed.
    if (drag.targetIdx !== drag.originIdx) {
      const newOrder = [...spaces.map((s) => s.id)];
      const [removed] = newOrder.splice(drag.originIdx, 1);
      if (removed !== undefined) {
        newOrder.splice(drag.targetIdx, 0, removed);
        // Pass fromIndex/toIndex so the action layer can emit space_reordered.
        actions.reorderSpaces(newOrder, drag.originIdx, drag.targetIdx);
      }
    }

    // M1 fix: guard releasePointerCapture with hasPointerCapture check.
    if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    }
  }, [spaces, actions]);

  const handleTabPointerCancel = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    dragRef.current = null;
    tabRefs.current.forEach((tabEl) => {
      if (!tabEl) return;
      tabEl.classList.remove('spaces-tab--dragging');
      tabEl.style.transform = '';
      tabEl.style.transition = '';
    });
    // M1 fix: also guard releasePointerCapture on cancel.
    if (drag?.active && (e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    }
  }, []);

  return (
    <>
      <div className="spaces-tab-strip" role="tablist" aria-label="Spaces">
        {spaces.map((space, idx) => {
          const isActive = space.id === activeSpaceId;
          const isRenaming = renameInlineState?.spaceId === space.id;
          const tid = tabId(space.id);

          return (
            <div
              key={space.id}
              ref={(el) => { tabRefs.current[idx] = el; }}
              className="spaces-tab-wrapper"
              data-active={isActive ? 'true' : undefined}
              onPointerDown={(e) => { handleTabPointerDown(e, space.id, idx); }}
              onPointerMove={(e) => { handleTabPointerMove(e, idx); }}
              onPointerUp={handleTabPointerUp}
              onPointerCancel={handleTabPointerCancel}
            >
              {isRenaming ? (
                /* Inline rename: input replaces the tab button visually */
                <div className={`spaces-tab spaces-tab--renaming${isActive ? ' spaces-tab--active' : ''}`}>
                  <input
                    ref={inlineInputRef}
                    className="spaces-tab-rename-input"
                    defaultValue={space.name}
                    maxLength={MAX_SPACE_NAME_CHARS}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        renameSettledRef.current = true; // latch before commit so onBlur no-ops
                        commitInlineRename((e.target as HTMLInputElement).value);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        renameSettledRef.current = true; // latch before cancel so onBlur no-ops
                        cancelInlineRename();
                      }
                    }}
                    onBlur={(e) => {
                      if (!renameSettledRef.current) commitInlineRename(e.target.value);
                      renameSettledRef.current = false;
                    }}
                    onClick={(e) => { e.stopPropagation(); }}
                  />
                </div>
              ) : (
                /* Normal tab button — no nested buttons inside */
                <button
                  type="button"
                  role="tab"
                  id={tid}
                  aria-selected={isActive}
                  aria-controls="board-panel"
                  tabIndex={isActive ? 0 : -1}
                  className="spaces-tab"
                  onClick={() => { actions.switchSpace(space.id, 'tab'); }}
                  onKeyDown={(e) => { handleTabKeyDown(e, idx); }}
                  onDoubleClick={() => {
                    renameSettledRef.current = false; // reset latch for new session
                    startInlineRename(space.id, space.name);
                  }}
                >
                  {space.name}
                </button>
              )}
              {/* ··· options button — sibling (not nested) to keep valid HTML */}
              <button
                type="button"
                className="spaces-tab-options"
                aria-label={`Options for ${space.name}`}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  openPopover(space.id, e.currentTarget);
                }}
              >
                <span aria-hidden="true">···</span>
              </button>
            </div>
          );
        })}

        <button
          type="button"
          className="spaces-tab-add"
          aria-label="Add a space"
          disabled={atLimit}
          title={atLimit ? `Limit of ${MAX_SPACES} spaces reached` : 'Add a new space'}
          onClick={() => {
            if (!atLimit) {
              setPromptMode({ kind: 'create' });
              setPromptError(null);
            }
          }}
        >
          +
        </button>
      </div>

      {/* Inline rename error message below the strip */}
      {inlineRenameError && (
        <p role="alert" className="spaces-rename-error">
          {inlineRenameError}
        </p>
      )}

      {/* Popover (context menu for a space) */}
      {popover && (() => {
        const space = spaces.find((s) => s.id === popover.spaceId);
        if (!space) return null;
        return (
          <SpacePopover
            spaceId={popover.spaceId}
            spaceName={space.name}
            isLast={spaces.length <= 1}
            anchorRect={popover.anchorRect}
            onRename={() => { handlePopoverRename(popover.spaceId, space.name); }}
            onDuplicate={() => { handlePopoverDuplicate(popover.spaceId); }}
            onDelete={() => { handlePopoverDelete(popover.spaceId); }}
            onClose={closePopover}
          />
        );
      })()}

      {/* Name prompt (create or rename via popover) */}
      {promptMode && (
        <SpaceNamePrompt
          title={promptMode.kind === 'create' ? 'New space' : 'Rename space'}
          initialValue={promptMode.kind === 'rename' ? promptMode.currentName : ''}
          errorMessage={promptError}
          onConfirm={handlePromptConfirm}
          onCancel={handlePromptCancel}
        />
      )}
    </>
  );
}
